import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable } from 'rxjs';

import { Environment } from '../../../environment';
import {
  FlockDataParameter,
  GetBenchmarksRequest,
  GetFlockDataRequest,
  QueryableFlockData,
  TimeSeriesResponse,
} from '../models/farmer-insights.model';

@Injectable({
  providedIn: 'root',
})
export class FlockDataService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private baseUrl = `${this.env.API_BASE_URL}/service/flock-report/secure`;

  private readonly benchmarkCapableParameters: FlockDataParameter[] = [
    FlockDataParameter.WeightedHeelDermatitis,
    FlockDataParameter.WeightedFootpadLesionNonePercentage,
    FlockDataParameter.WeightedFootpadLesionModeratePercentage,
    FlockDataParameter.WeightedFootpadLesionSeverePercentage,
    FlockDataParameter.LossPercentage,
    FlockDataParameter.WeightedFootpadLesionScore,
    FlockDataParameter.ReplacementPercentage,
    FlockDataParameter.FeedConversion,
    FlockDataParameter.FeedConversion1500,
    FlockDataParameter.AverageWeightInGram,
    FlockDataParameter.WeightedAverageAgeInDays,
    FlockDataParameter.WeightedAverageGrowthPerDay,
    FlockDataParameter.ProductionNumber,
    FlockDataParameter.DayOldChicksPerSquareMeter,
    FlockDataParameter.FeedUsageInKgPerSquareMeter,
    FlockDataParameter.PricePerDayOldChick,
    FlockDataParameter.FeedConsumptionPricePer100Kg,
    FlockDataParameter.PaidAmountPerKg,
    FlockDataParameter.NettoRevenuePerChick,
    FlockDataParameter.NettoRevenuePerSquareMeter,
    FlockDataParameter.NettoRevenuePerSquareMeterPerDay,
    FlockDataParameter.FeedConsumptionPerDeliveredKg,
    FlockDataParameter.RejectionRate,
    FlockDataParameter.DeliveredKgPerSquareMeter,
  ];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  getFlockData(
    getFlockDataRequest: GetFlockDataRequest,
  ): Observable<TimeSeriesResponse | HttpErrorResponse> {
    return this.http.post<TimeSeriesResponse>(`${this.baseUrl}/flockdata`, getFlockDataRequest, {});
  }

  isLowerValueBetterForKpi(kpi: FlockDataParameter) {
    switch (kpi) {
      case FlockDataParameter.WeightedHeelDermatitis:
      case FlockDataParameter.WeightedFootpadLesionNonePercentage:
      case FlockDataParameter.WeightedFootpadLesionModeratePercentage:
      case FlockDataParameter.WeightedFootpadLesionSeverePercentage:
      case FlockDataParameter.LossPercentage:
      case FlockDataParameter.WeightedFootpadLesionScore:
      case FlockDataParameter.FeedConversion:
      case FlockDataParameter.FeedConversion1500:
      case FlockDataParameter.DayOldChicksPerSquareMeter:
      case FlockDataParameter.FeedUsageInKgPerSquareMeter:
      case FlockDataParameter.PricePerDayOldChick:
      case FlockDataParameter.FeedConsumptionPerDeliveredKg:
      case FlockDataParameter.RejectionRate:
        return true;
      default:
        return false;
    }
  }

  showDeviationColorForKpi(kpi: FlockDataParameter) {
    switch (kpi) {
      case FlockDataParameter.ReplacementPercentage:
      case FlockDataParameter.WeightedAverageAgeInDays:
      case FlockDataParameter.FeedConsumptionPricePer100Kg:
        return false;
      default:
        return true;
    }
  }

  getBenchmarks(
    getBenchmarksRequest: GetBenchmarksRequest,
  ): Observable<TimeSeriesResponse | HttpErrorResponse> {
    // Intersect the array of parameters with those capable of generating a benchmark.
    const request: GetBenchmarksRequest = {
      ...getBenchmarksRequest,
      parameters: getBenchmarksRequest.parameters.filter((p) =>
        this.benchmarkCapableParameters.includes(p),
      ),
    };
    return this.http.post<TimeSeriesResponse>(`${this.baseUrl}/flockdata/benchmarks`, request, {});
  }

  getCustomerAverageBenchmarks(
    getBenchmarksRequest: GetBenchmarksRequest,
  ): Observable<TimeSeriesResponse | HttpErrorResponse> {
    // Intersect the array of parameters with those capable of generating a benchmark.
    const request: GetBenchmarksRequest = {
      ...getBenchmarksRequest,
      parameters: getBenchmarksRequest.parameters.filter((p) =>
        this.benchmarkCapableParameters.includes(p),
      ),
    };
    return this.http.post<TimeSeriesResponse>(
      `${this.baseUrl}/flockdata/customer-benchmarks`,
      request,
    );
  }

  getQueryableFlockData(): Observable<QueryableFlockData | HttpErrorResponse> {
    return this.http.get<QueryableFlockData>(`${this.baseUrl}/flockdata/queries`, {});
  }
}
